import { ca, newConfiguration } from './ldapPropTypes';

import { Fragment, PureComponent } from 'react';
import {
  Field,
  FieldArray,
  Form,
  formValues,
  reduxForm,
} from 'redux-form/immutable';
import {
  AD_DEFAULTS,
  DELETE_CONFIG_DIALOG_NAME,
  OPENLDAP_DEFAULTS,
  PAGE_URL,
} from './constants';

import { isNullable } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import FormErrorMessage from 'components/FormErrorMessage';
import MultiSelectField from 'components/MultiSelectField';
import PasswordField from 'components/PasswordField';
import SelectField from 'components/SelectField';
import TagsInputField from 'components/TagsInputField';
import TextField from 'components/TextField';
import ToggleField from 'components/ToggleField';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import LTEBox from 'containers/LTEBox';
import LeaveDialogue from 'containers/LeaveDialogue';
import { List } from 'immutable';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import handleEvent from 'utils/handleEvent';
import FileUpload from './FileUpload';
import HiddenField from './HiddenField';
import RenderServers from './RenderServers';

const accountSuffixesNotUniq = (
  configurationId,
  configurations,
  accountSuffixes,
) => {
  return configurations.reduce((acc, configuration) => {
    const id = configuration.get('id');
    if (id === configurationId) {
      return acc;
    }
    const configurationHasAccountSuffixes = configuration
      .get('accountSuffixes', new List())
      .reduce((accSuffix, val) => {
        if (accountSuffixes.indexOf(val) > -1) {
          return accSuffix.concat({ type: 'suffixUniq', value: val });
        }
        return accSuffix;
      }, []);
    return configurationHasAccountSuffixes.length
      ? acc.concat(configurationHasAccountSuffixes)
      : acc;
  }, []);
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
const validate = (values, props) => {
  const { configuration, configurationId } = props;
  const errors = {};
  const accountSuffixes = values.get('accountSuffixes');
  const serverType = values.get('serverType');
  const servers = values.get('server');
  const bindDN = values.get('bindDN');
  const bindCredentials = values.get('bindCredentials');
  const searchBase = values.get('searchBase');
  const searchLogin = values.get('searchLogin');
  const id = values.get('id');
  if (!accountSuffixes || !accountSuffixes.size) {
    errors.accountSuffixes = 'required';
  } else {
    const suffixesNotUniq = accountSuffixesNotUniq(
      configurationId,
      configuration,
      accountSuffixes,
    );
    if (suffixesNotUniq.length) {
      errors.accountSuffixes = suffixesNotUniq;
    }
    const suffixesNotHaveAt = accountSuffixes.reduce((acc, suffix) => {
      if (!suffix.startsWith('@')) {
        return acc.concat({ type: 'suffixStartsWith', value: suffix });
      }
      return acc;
    }, []);
    if (suffixesNotHaveAt.length) {
      errors.accountSuffixes = suffixesNotHaveAt;
    }
  }
  if (serverType === 'list') {
    if (!servers || !servers.size) {
      errors.servers = { _errors: 'requiredServers' };
    } else {
      const serverArrayErrors = [];
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
      servers.forEach((server, serverIndex) => {
        const serverErrors = {};
        const url = server.get('url');
        if (!url) {
          serverErrors.url = 'required';
        } else if (!/^ldap(s)?:\/\//.test(url)) {
          serverErrors.url = 'urlStartsWith';
        }
        const port = server.get('port');
        if (isNullable(port) || port === '') {
          serverErrors.port = 'required';
          // biome-ignore lint/suspicious/noGlobalIsNan: unsafe to fix in untyped code, fix with refactoring to typed code
        } else if (isNaN(port)) {
          serverErrors.port = 'number';
        }
        if (Object.keys(serverErrors).length) {
          serverArrayErrors[serverIndex] = serverErrors;
        }
      });
      if (serverArrayErrors.length) {
        errors.server = serverArrayErrors;
      }
    }
  } else if (serverType === 'dns') {
    const domain = values.get('domain');
    const serviceName = values.get('serviceName');
    if (!domain) {
      errors.domain = 'required';
    }
    if (!serviceName) {
      errors.serviceName = 'required';
    }
  }
  if (!bindDN) {
    errors.bindDN = 'required';
  }
  if (id === 'new' && !bindCredentials) {
    errors.bindCredentials = 'required';
  }
  if (!searchBase) {
    errors.searchBase = 'required';
  }
  if (!searchLogin || !searchLogin.size) {
    errors.searchLogin = 'required';
  }
  return errors;
};

@reduxForm({
  enableReinitialize: true,
  validate,
  pure: false,
})
@formValues('serverType', 'type')
export default class ConfigForm extends PureComponent {
  static propTypes = {
    anyTouched: PropTypes.bool,
    ca: ca,
    change: PropTypes.func.isRequired,
    configurationId: PropTypes.string.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    leaveDialogueMessages: PropTypes.object.isRequired,
    msg: PropTypes.object.isRequired,
    newConfiguration: newConfiguration,
    onDeleteConfiguration: PropTypes.func.isRequired,
    onDomainOptionsChange: PropTypes.func.isRequired,
    onFetchCA: PropTypes.func.isRequired,
    onOpenFileUpload: PropTypes.func.isRequired,
    onSetReturnUrl: PropTypes.func.isRequired,
    onToggleConfirmDialog: PropTypes.func.isRequired,
    pristine: PropTypes.bool,
    submitting: PropTypes.bool.isRequired,
    type: PropTypes.string,
    touch: PropTypes.func,
    error: PropTypes.string,
    serverType: PropTypes.string,
    valid: PropTypes.bool,
  };

  componentDidMount() {
    this.props.onFetchCA();
  }

  handleFieldChange = handleEvent(() => {
    const { onDomainOptionsChange } = this.props;
    onDomainOptionsChange({ dataModified: true });
  });

  handleTypeChange = (_e, selectedItem) => {
    const { change } = this.props;
    if (selectedItem === 'ad') {
      change('searchLogin', AD_DEFAULTS.searchLogin);
    } else if (selectedItem === 'openLdap') {
      change('searchLogin', OPENLDAP_DEFAULTS.searchLogin);
    }
    this.handleFieldChange();
  };

  handleSave = handleEvent(() => {
    const { handleSubmit } = this.props;
    handleSubmit();
  });

  handleCloseEditConfiguration = handleEvent(() => {
    const { navigate } = this.props;
    navigate(PAGE_URL);
  });

  handleLeaveDialogueSave = handleEvent((returnUrl) => {
    const { handleSubmit, onSetReturnUrl } = this.props;
    onSetReturnUrl(returnUrl);
    handleSubmit();
  });

  handleDeleteConfiguration = handleEvent((e) => {
    e.preventDefault();
    this.props.onToggleConfirmDialog(DELETE_CONFIG_DIALOG_NAME);
  });

  handleDeleteConfigurationConfirm = handleEvent(() => {
    const { onDeleteConfiguration, configurationId } = this.props;
    onDeleteConfiguration(configurationId);
  });

  handleShowUploadFile = handleEvent(() => {
    this.props.onOpenFileUpload();
  });

  render() {
    const {
      anyTouched,
      ca,
      change,
      error,
      handleSubmit,
      leaveDialogueMessages,
      msg,
      newConfiguration,
      onOpenFileUpload,
      pristine,
      serverType,
      submitting,
      touch,
      type,
      valid,
    } = this.props;
    const typeOptions = [
      { label: get(msg, 'configurations.type.ad'), value: 'ad' },
      { label: get(msg, 'configurations.type.openLdap'), value: 'openLdap' },
    ];
    const serverTypeOptions = [
      { label: get(msg, 'configurations.server.list'), value: 'list' },
      { label: get(msg, 'configurations.server.dns'), value: 'dns' },
    ];
    const certificateOptions = ca
      .map((item) => ({
        value: item.get('id'),
        label: item.get('name'),
      }))
      .toJS();
    return (
      <div>
        <LeaveDialogue
          isPristine={pristine}
          isValid={valid}
          msg={{
            ...leaveDialogueMessages,
            btnSave: submitting
              ? get(msg, 'btnLabelDuringSaving')
              : leaveDialogueMessages.btnSave,
          }}
          onSave={this.handleLeaveDialogueSave}
          saving={submitting}
        />
        <ConfirmDialog
          id={DELETE_CONFIG_DIALOG_NAME}
          onClickConfirm={this.handleDeleteConfigurationConfirm}
          msg={msg.configurations.deleteConfig}
          btnConfirmVariant='primary'
          btnConfirmAccent='negative'
          btnCancelVariant='secondary'
          btnCancelAccent='positive'
        />
        <FileUpload />
        <Form onSubmit={handleSubmit} autoComplete='off'>
          <fieldset className='p-0' disabled={submitting}>
            <div>
              <Field
                className='form-control'
                clearable={false}
                component={HiddenField}
                id='id'
                msg={msg}
                name='id'
                type='hidden'
              />
              <div className='row'>
                <div className='col-md-6'>
                  <div className='form-group'>
                    <label htmlFor={undefined} className='block'>
                      {get(msg, ['configurations', 'type', 'label'])}
                    </label>
                    <Field
                      clearable={false}
                      component={SelectField}
                      disabled={submitting}
                      msg={msg}
                      name='type'
                      onChange={this.handleTypeChange}
                      options={typeOptions}
                      searchable={false}
                      touch={touch}
                      type='text'
                      wrapperClassName='configuration__type'
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='form-group'>
                    <label htmlFor={undefined} className='block'>
                      {get(msg, ['configurations', 'accountSuffixes'])}
                    </label>
                    <Field
                      component={TagsInputField}
                      disabled={submitting}
                      msg={msg}
                      name='accountSuffixes'
                      onChange={this.handleFieldChange}
                      placeholder={get(msg, [
                        'configurations',
                        'accountSuffixesPlaceholder',
                      ])}
                      touch={touch}
                      type='text'
                      wrapperClassName='ldap-settings__account-suffixes--wrapper'
                    />
                    <span className='help-block'>
                      {get(msg, ['configurations', 'accountSuffixesHelp'])}
                    </span>
                  </div>
                </div>
              </div>
              <LTEBox
                bodyComponent={
                  <div>
                    <div className='mb-lgu-5'>
                      <Field
                        clearable={false}
                        component={SelectField}
                        disabled={submitting}
                        id='filterSelector'
                        name='serverType'
                        onChange={this.handleShowChange}
                        options={serverTypeOptions}
                        searchable={false}
                        type='select'
                        wrapperClassName='server__type'
                      />
                    </div>
                    {serverType === 'list' ? (
                      <FieldArray
                        ca={ca}
                        change={change}
                        component={RenderServers}
                        msg={msg}
                        name='server'
                        onChange={this.handleFieldChange}
                        onOpenFileUpload={onOpenFileUpload}
                        formSubmitting={submitting}
                      />
                    ) : null}
                    {serverType === 'dns' ? (
                      <Fragment>
                        <div className='row'>
                          <div className='form-group col-xs-12'>
                            <label htmlFor={undefined} className='block'>
                              {get(msg, 'configurations.server.cert')}
                            </label>
                            <div>
                              <div className='ldap-settings__select-cert mr-lgu-5 float-left'>
                                <Field
                                  block
                                  className='form-control'
                                  component={MultiSelectField}
                                  disabled={submitting}
                                  msg={msg}
                                  name='cert'
                                  onChange={this.handleFieldChange}
                                  options={certificateOptions}
                                  styles={{
                                    clearIndicator: (base) => ({
                                      ...base,
                                      padding: '6px 8px',
                                    }),
                                    control: (base) => ({
                                      ...base,
                                      minHeight: 34,
                                    }),
                                    dropdownIndicator: (base) => ({
                                      ...base,
                                      padding: '6px 8px',
                                    }),
                                  }}
                                  type='text'
                                />
                              </div>
                              <Button
                                variant='secondary'
                                className='float-left'
                                disabled={submitting}
                                onClick={this.handleShowUploadFile}
                                type='button'
                                icon='fa-upload'
                              >
                                {get(msg, [
                                  'configurations',
                                  'server',
                                  'btnUpload',
                                ])}
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className='row'>
                          <div className='form-group col-md-6'>
                            <label htmlFor={undefined} className='block'>
                              {get(msg, 'configurations.server.domain')}
                            </label>
                            <Field
                              block
                              className='form-control'
                              component={TextField}
                              msg={msg}
                              name='domain'
                              onChange={this.handleFieldChange}
                            />
                            <span className='help-block'>
                              {get(msg, 'configurations.server.domainHelp')}
                            </span>
                          </div>
                          <div className='form-group col-md-6'>
                            <label htmlFor={undefined} className='block'>
                              {get(msg, 'configurations.server.serviceName')}
                            </label>
                            <Field
                              block
                              className='form-control'
                              component={TextField}
                              msg={msg}
                              name='serviceName'
                              onChange={this.handleFieldChange}
                            />
                            <span className='help-block'>
                              {get(
                                msg,
                                'configurations.server.serviceNameHelp',
                              )}
                            </span>
                          </div>
                        </div>
                        <div className='form-group mb-0'>
                          <Field
                            className=''
                            component={ToggleField}
                            msg={msg}
                            name='useLdaps'
                            type='checkbox'
                            onChange={this.handleFieldChange}
                            id='useLdaps'
                            hideStateMessage
                          />
                          <label className='ml-lgu-3' htmlFor='useLdaps'>
                            {get(msg, 'configurations.server.useLdaps')}
                          </label>
                        </div>
                      </Fragment>
                    ) : null}
                  </div>
                }
                className='box box-test-connection'
                title={get(msg, ['configurations', 'server', 'label'])}
              />
              <div className='row'>
                <div className='form-group col-md-6'>
                  <label htmlFor={undefined} className='block'>
                    {get(msg, ['configurations', 'bindDN'])}
                  </label>
                  <Field
                    block
                    className='form-control'
                    component={TextField}
                    msg={msg}
                    name='bindDN'
                    onChange={this.handleFieldChange}
                  />
                  <span className='help-block'>
                    {get(msg, ['configurations', 'bindDNHelp'])}
                  </span>
                </div>
                <div className='form-group col-md-6'>
                  <label htmlFor={undefined} className='block'>
                    {get(msg, ['configurations', 'bindCredentials'])}
                  </label>
                  <Field
                    block
                    className='form-control'
                    component={PasswordField}
                    msg={msg}
                    name='bindCredentials'
                    onChange={this.handleFieldChange}
                  />
                  <span className='help-block'>
                    {get(msg, ['configurations', 'bindCredentialsHelp'])}
                  </span>
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-md-6'>
                  <label htmlFor={undefined} className='block'>
                    {get(msg, ['configurations', 'searchBase'])}
                  </label>
                  <Field
                    block
                    className='form-control'
                    component={TextField}
                    msg={msg}
                    name='searchBase'
                    onChange={this.handleFieldChange}
                  />
                  <span className='help-block'>
                    {get(msg, ['configurations', 'searchBaseHelp'])}
                  </span>
                </div>
                <div className='form-group col-md-6'>
                  <label htmlFor={undefined} className='block'>
                    {get(msg, ['configurations', 'searchLogin'])}
                  </label>
                  <Field
                    block
                    component={TagsInputField}
                    msg={msg}
                    name='searchLogin'
                    disabled={submitting}
                    onChange={this.handleFieldChange}
                    touch={touch}
                    type='text'
                    wrapperClassName='ldap-search-login'
                  />
                </div>
              </div>
              <div className='row'>
                {type === 'ad' && (
                  <div className='form-group col-md-6'>
                    <label htmlFor={undefined} className='block'>
                      {get(msg, ['configurations', 'nestedGroups'])}
                    </label>
                    <Field
                      className='form-control'
                      component={ToggleField}
                      disabled={submitting}
                      id='nestedGroups'
                      msg={msg}
                      name='nestedGroups'
                      type='checkbox'
                      hideStateMessage
                      label={get(msg, ['configurations', 'allowNestedGroups'])}
                      labelPosition='right'
                    />
                  </div>
                )}
              </div>
            </div>
            {anyTouched && error && typeof error === 'string' && (
              <FormErrorMessage>
                {get(msg, ['validation', error], 'Error')}
              </FormErrorMessage>
            )}
            <div className='mt-3'>
              <Button
                type='button'
                variant='primary'
                icon='fa-check'
                onClick={this.handleSave}
                disabled={submitting}
                className='mr-2'
                loading={submitting}
              >
                {submitting
                  ? get(msg, 'btnLabelDuringSaving')
                  : get(msg, 'btnConfirm')}
              </Button>
              {!newConfiguration ? (
                <Button
                  type='button'
                  variant='primary'
                  accent='negative'
                  icon='fa-trash-can'
                  className='mr-2'
                  onClick={this.handleDeleteConfiguration}
                  disabled={submitting}
                >
                  {get(msg, 'btnRemove')}
                </Button>
              ) : null}
              <Button
                type='button'
                variant='secondary'
                icon='fa-xmark'
                onClick={this.handleCloseEditConfiguration}
                disabled={submitting}
              >
                {get(msg, 'btnCancel')}
              </Button>
            </div>
          </fieldset>
        </Form>
      </div>
    );
  }
}
